// ═══════════════════════════════════════════════════════════════════════════
// PAGE: BASCULE
// Migration S5.3 du plan TS+Vite (2026-05-10) : .js → .ts strict.
// ═══════════════════════════════════════════════════════════════════════════
//
// Mai 2026 : la page bascule est devenue une modale unique (showBasculeWizard).
// La fonction renderBasculePage est conservée pour rétrocompat.

import { definePage } from '../router.js';
import { buildBasculeInstanceRow } from './page-bascule-instance-row.js';

export interface BasculeUser {
  id: number;
  name: string;
  trigram?: string | null;
  is_team?: boolean;
}

export interface BasculeInstance {
  id: number;
  order: number;
  label: string;
  // RACI template (rôles génériques). A = Approuve (single) ; R = Réalise (multi).
  accountable_role?: string | null;
  responsible_roles?: string[];
  consulted_roles?: string[];
  informed_roles?: string[];
  approver_id?: number | null;
  approver_name?: string | null;
  performers?: number[];
  consulted?: number[];
  informed?: number[];
  deadline?: string | null;
  action_id?: number | null;
}

interface RoleMappingValue {
  user_id: number | null;
  user_name?: string;
}

interface BasculeAppData {
  id: number;
  template_name: string;
  project_id?: number;
  total?: number;
  complete?: number;
  pct?: number;
  instances: BasculeInstance[];
  role_mappings?: Record<string, RoleMappingValue>;
}

interface ProjectMin {
  id: number;
  code?: string | null;
  name: string;
}

interface ActionStatusLite {
  id: number;
  name: string;
}

declare global {
  interface Window {
    showBasculeWizard: (
      appData: BasculeAppData,
      users: BasculeUser[],
      statuses: ActionStatusLite[],
      allProjects: ProjectMin[],
    ) => Promise<void>;
    renderBasculePage: typeof renderBasculePage;
  }
}

pages.bascule = definePage<'bascule/:appId'>(async ({ appId }) => {
  setHeader(t('bascule.loading.title'), '');
  setContent(`<div class="loading">${esc(t('bascule.loading.body'))}</div>`);
  try {
    const [appData, users, statuses, allProjects] = (await Promise.all([
      api.getApplication(appId),
      loadUsers(),
      loadActionStatuses(),
      api.getProjects(),
    ])) as unknown as [BasculeAppData, BasculeUser[], ActionStatusLite[], ProjectMin[]];
    router.navigate('#actions');
    setTimeout(() => void window.showBasculeWizard(appData, users, statuses, allProjects), 0);
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : t('common.error');
    setContent(`<div class="error">${esc(msg)}</div>`);
  }
});

// Charge l'action liée à une instance de bascule, en la créant d'abord si
// l'instance n'en a pas encore. `getAction` lit UNE action ; la version
// précédente téléchargeait `GET /actions` en entier — sans `include_completed`,
// donc terminées et fusionnées comprises — pour n'en retenir qu'une par `find()`.
// Sur un tenant chargé cela faisait plusieurs centaines de Ko et près d'un
// millier de requêtes SQL par ouverture de la modale (cf. docs/PERF.md).
// Les DEUX appels sont couverts : l'appelant tourne dans un `void (async …)()`,
// où une promesse rejetée ne remonte nulle part — le crayon resterait sans effet
// et sans message. Un échec de l'un ou l'autre est donc rabattu sur `undefined`,
// et l'appelant affiche le toast d'erreur d'ouverture.
async function _loadInstanceAction(inst: BasculeInstance): Promise<{ id: number } | undefined> {
  try {
    let actionId = inst.action_id;
    if (!actionId) {
      const created = (await api.createActionForInstance(inst.id)) as { action_id: number };
      actionId = created.action_id;
      inst.action_id = actionId;
    }
    return (await api.getAction(actionId)) as { id: number };
  } catch {
    return undefined;
  }
}

window.showBasculeWizard = async function (
  appData: BasculeAppData,
  users: BasculeUser[],
  statuses: ActionStatusLite[],
  allProjects: ProjectMin[],
): Promise<void> {
  if (!appData || !Array.isArray(appData.instances)) {
    toast(t('bascule.invalid'), 'error');
    return;
  }
  const userMap: Record<number, BasculeUser> = Object.fromEntries(users.map((u) => [u.id, u]));
  const mappings: Record<string, RoleMappingValue> = { ...(appData.role_mappings || {}) };
  let currentFilter: 'all' | 'incomplete' | 'nodate' | 'noresp' = 'all';

  const usedRoles = new Set<string>();
  appData.instances.forEach((inst) => {
    if (inst.accountable_role) usedRoles.add(inst.accountable_role);
    (inst.responsible_roles || []).forEach((r) => usedRoles.add(r));
    (inst.consulted_roles || []).forEach((r) => usedRoles.add(r));
    (inst.informed_roles || []).forEach((r) => usedRoles.add(r));
  });
  const roleList = [...usedRoles].sort();
  const sortedUsers = [...users].sort((a, b) =>
    a.is_team === b.is_team ? a.name.localeCompare(b.name) : a.is_team ? 1 : -1,
  );
  const userOptionsFor = (selectedId: number | string): string =>
    sortedUsers
      .map(
        (u) =>
          `<option value="${u.id}"${u.id === selectedId ? ' selected' : ''}>${u.is_team ? t('bascule.user.team', { name: u.name }) : esc(u.name)}</option>`,
      )
      .join('');

  const nameOf = (id: number): string => {
    const u = userMap[id];
    return u ? trigramHtml(u, { fallbackToName: true, placeholder: '?' }) : '?';
  };

  const filteredInstances = (): BasculeInstance[] => {
    let v = appData.instances;
    if (currentFilter === 'incomplete')
      v = v.filter((i) => !(i.approver_id && (i.performers || []).length && i.deadline));
    if (currentFilter === 'nodate') v = v.filter((i) => !i.deadline);
    if (currentFilter === 'noresp') v = v.filter((i) => !i.approver_id);
    return v;
  };

  function renderProgress(): string {
    const pct = Number.isFinite(appData.pct) ? (appData.pct ?? 0) : 0;
    const total = appData.total ?? appData.instances.length;
    const complete = appData.complete ?? 0;
    const col = pct >= 100 ? 'var(--ok)' : pct >= 50 ? 'var(--accent)' : 'var(--warn)';
    return `<div style="margin-bottom:12px">
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:6px">
        <div style="font-size:12px;font-weight:500;color:var(--text3);text-transform:uppercase;letter-spacing:.05em">${esc(t('bascule.progress.title'))}</div>
        <span style="font-size:13px;font-weight:500">${t('bascule.progress.value', { complete, total, pct })}</span>
      </div>
      <div style="height:8px;background:var(--bg3);border-radius:4px;overflow:hidden">
        <div style="height:100%;width:${pct}%;background:${col};transition:width .3s"></div>
      </div>
    </div>`;
  }

  function renderMapping(): string {
    if (!roleList.length) {
      return `<div class="card" style="margin-bottom:12px;font-size:12px;color:var(--text3)">
        ${esc(t('bascule.mapping.empty'))}
      </div>`;
    }
    return `<div class="card" style="margin-bottom:12px">
      <div style="font-size:12px;font-weight:500;color:var(--text3);text-transform:uppercase;letter-spacing:.05em;margin-bottom:6px">${esc(t('bascule.mapping.title'))}</div>
      <div style="font-size:12px;color:var(--text3);margin-bottom:10px">${esc(t('bascule.mapping.help'))}</div>
      <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(320px,1fr));gap:8px 16px">
        ${roleList
          .map((code) => {
            const mapped = mappings[code];
            const selectedId = mapped?.user_id ?? '';
            const isAssigned = !!mapped?.user_id;
            const dot = `<span title="${esc(t(isAssigned ? 'bascule.role.assigned' : 'bascule.role.unassigned'))}" style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${isAssigned ? 'var(--ok)' : 'var(--text3)'};flex-shrink:0"></span>`;
            return `<div style="display:flex;align-items:center;gap:8px">
            ${dot}
            <span style="font-family:var(--font-mono);font-size:12px;font-weight:600;min-width:50px;background:var(--bg3);padding:2px 8px;border-radius:4px">${esc(code)}</span>
            <select class="bw-map-select" data-role="${esc(code)}" style="flex:1;min-width:140px;max-width:240px;font-size:12px;padding:4px 6px;border:1px solid var(--border2);border-radius:var(--radius);background:var(--bg)">
              <option value=""${selectedId === '' ? ' selected' : ''}>${esc(t('bascule.role.placeholder'))}</option>
              ${userOptionsFor(selectedId)}
            </select>
          </div>`;
          })
          .join('')}
      </div>
      <div style="margin-top:12px;border-top:1px solid var(--border);padding-top:10px">
        <button class="btn btn-primary btn-sm" id="bw-apply-mappings" type="button">${esc(t('bascule.btn.applyMapping'))}</button>
      </div>
    </div>`;
  }

  function renderFilters(): string {
    const opts: Array<[typeof currentFilter, string]> = [
      ['all', t('bascule.filter.all')],
      ['incomplete', t('bascule.filter.incomplete')],
      ['nodate', t('bascule.filter.nodate')],
      ['noresp', t('bascule.filter.noresp')],
    ];
    const count = filteredInstances().length;
    return `<div style="display:flex;align-items:center;gap:6px;margin-bottom:8px;flex-wrap:wrap">
      <div id="bw-actions-count" style="font-size:12px;font-weight:500;color:var(--text3);text-transform:uppercase;letter-spacing:.05em;flex:1;min-width:140px">${t('bascule.actions.count', { n: count })}</div>
      ${opts.map(([k, lbl]) => `<button type="button" class="btn btn-secondary btn-sm bw-filter${k === currentFilter ? ' active' : ''}" data-bf="${k}">${esc(lbl)}</button>`).join('')}
    </div>`;
  }

  function instanceRow(inst: BasculeInstance): string {
    const a = inst;
    const approverName = a.approver_id
      ? userMap[a.approver_id]?.name || a.approver_name || '-'
      : '-';
    const performers = (a.performers || []).map(nameOf).join(', ') || '-';
    const consulted = (a.consulted || []).map(nameOf).join(', ') || '-';
    const informed = (a.informed || []).map(nameOf).join(', ') || '-';
    const isComplete = a.approver_id && (a.performers || []).length > 0 && a.deadline;
    const statusColor = isComplete
      ? 'var(--ok)'
      : a.approver_id || (a.performers || []).length || a.deadline
        ? 'var(--warn)'
        : 'var(--text3)';
    const statusDot = `<span style="width:8px;height:8px;border-radius:50%;background:${statusColor};display:inline-block;margin-right:4px"></span>`;
    const raciItem = (lbl: string, val: string): string => `<div style="min-width:80px">
      <span style="font-size:10px;color:var(--text3);text-transform:uppercase;letter-spacing:.05em">${lbl}</span>
      <div style="font-size:12px;margin-top:1px">${val}</div>
    </div>`;
    return `<div class="card" style="margin-bottom:6px;cursor:pointer;padding:10px 12px" data-bw-inst-id="${inst.id}">
      <div style="display:flex;align-items:flex-start;justify-content:space-between;gap:12px">
        <div style="flex:1">
          <div style="font-size:13px;font-weight:500">${inst.order + 1}. ${esc(inst.label)}</div>
          <div style="display:flex;gap:12px;margin-top:8px;flex-wrap:wrap">
            ${raciItem(esc(t('bascule.raci.r')), performers)}
            ${raciItem(esc(t('bascule.raci.a')), esc(approverName))}
            ${raciItem(esc(t('bascule.raci.c')), consulted)}
            ${raciItem(esc(t('bascule.raci.i')), informed)}
            ${raciItem(esc(t('bascule.raci.deadline')), esc(fmtDate(a.deadline)) || '—')}
          </div>
        </div>
        <div style="display:flex;align-items:center;gap:6px;flex-shrink:0">
          ${statusDot}
          <button type="button" class="btn btn-secondary btn-icon bw-edit-inst" data-bw-edit="${inst.id}" title="${esc(t('common.edit'))}" aria-label="${esc(t('common.edit'))}" style="font-size:13px">✎</button>
        </div>
      </div>
    </div>`;
  }

  function renderInstancesList(): string {
    const visible = filteredInstances();
    if (!visible.length) {
      return `<p style="color:var(--text3);font-size:12px;padding:16px 0">${esc(t('bascule.empty.filter'))}</p>`;
    }
    return visible.map(instanceRow).join('');
  }

  function modalBody(): string {
    return `
      <div style="font-size:12px;color:var(--text3);margin-bottom:10px">
        ${esc(t('bascule.modal.subtitle'))}
      </div>
      ${renderProgress()}
      ${renderMapping()}
      ${renderFilters()}
      <div id="bw-instances">${renderInstancesList()}</div>
    `;
  }

  async function reloadAppData(): Promise<void> {
    try {
      const fresh = (await api.getApplication(appData.id)) as BasculeAppData;
      Object.assign(appData, fresh);
    } catch {
      /* non bloquant */
    }
  }

  function refreshAll(): void {
    const body = document.getElementById('modal-body');

    if (body) body.innerHTML = modalBody();
    wireBody();
  }

  function refreshInstancesOnly(): void {
    const el = document.getElementById('bw-instances');

    if (el) el.innerHTML = renderInstancesList();
    const countEl = document.getElementById('bw-actions-count');
    if (countEl)
      countEl.textContent = t('bascule.actions.count', { n: filteredInstances().length });
    wireInstances();
  }

  function wireInstances(): void {
    document.querySelectorAll<HTMLButtonElement>('.bw-edit-inst').forEach((btn) => {
      btn.addEventListener('click', (e: Event) => {
        e.stopPropagation();
        void (async () => {
          const instId = parseInt(btn.dataset['bwEdit'] || '0');
          const inst = appData.instances.find((i) => i.id === instId);
          if (!inst) return;
          const actionData = await _loadInstanceAction(inst);
          if (!actionData) {
            toast(t('bascule.toast.openError'), 'error');
            return;
          }
          let handled = false;
          window.showEditActionModal(
            actionData as never,
            {
              users: users as never,
              statuses: statuses as never,
              projects: allProjects as never,
              onSaved: async () => {
                handled = true;
                await reloadAppData();
                await window.showBasculeWizard(appData, users, statuses, allProjects);
              },
            } as never,
          );
          // Re-ouvre le wizard si l'utilisateur annule (Annuler / ✕ / Esc /
          // clic fond). `onSaved` met `handled = true` pour éviter le
          // double rendu — le `setTimeout(…, 0)` laisse le micro-task de
          // `onSaved` poser le flag avant qu'on vérifie.
          setTimeout(() => {
            const obs = new MutationObserver(() => {
              if (!document.getElementById('modal-overlay')) {
                obs.disconnect();
                setTimeout(() => {
                  if (!handled) {
                    void window.showBasculeWizard(appData, users, statuses, allProjects);
                  }
                }, 0);
              }
            });
            obs.observe(document.body, { childList: true });
          }, 0);
        })();
      });
    });
    document.querySelectorAll<HTMLElement>('[data-bw-inst-id]').forEach((card) => {
      card.addEventListener('click', (e: Event) => {
        const target = e.target as HTMLElement | null;
        if (target?.closest('button')) return;
        card.querySelector<HTMLButtonElement>('[data-bw-edit]')?.click();
      });
    });
  }

  function wireBody(): void {
    document.querySelectorAll<HTMLSelectElement>('.bw-map-select').forEach((sel) => {
      sel.addEventListener('change', () => {
        void (async () => {
          const roleCode = sel.dataset['role'] || '';
          const uid = sel.value ? parseInt(sel.value) : null;
          if (uid) {
            const u = userMap[uid];
            mappings[roleCode] = { user_id: uid, user_name: u?.name || '' };
          } else {
            delete mappings[roleCode];
          }
          try {
            await api.setMappings(appData.id, {
              mappings: Object.entries(mappings).map(([role_code, v]) => ({
                role_code,
                user_id: v?.user_id || null,
              })),
            });
            appData.role_mappings = mappings;
          } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            toast(t('bascule.toast.mappingError', { msg }), 'error');
          }
          const dot = sel.parentElement?.querySelector<HTMLElement>('span[title]');
          if (dot) {
            const isAssigned = !!uid;
            dot.style.background = isAssigned ? 'var(--ok)' : 'var(--text3)';
            dot.title = t(isAssigned ? 'bascule.role.assigned' : 'bascule.role.unassigned');
          }
        })();
      });
    });
    const applyBtn = document.getElementById('bw-apply-mappings');
    if (applyBtn) {
      applyBtn.addEventListener('click', () => {
        void (async () => {
          try {
            const result = (await api.applyMappings(appData.id)) as BasculeAppData & {
              changes?: number;
            };
            Object.assign(appData, result);
            toast(t('bascule.toast.mappingApplied', { n: result.changes ?? 0 }));
            refreshAll();
          } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : t('bascule.toast.mappingApplyError');
            toast(msg, 'error');
          }
        })();
      });
    }
    document.querySelectorAll<HTMLButtonElement>('.bw-filter').forEach((btn) => {
      btn.addEventListener('click', () => {
        currentFilter = (btn.dataset['bf'] || 'all') as typeof currentFilter;
        document
          .querySelectorAll<HTMLButtonElement>('.bw-filter')
          .forEach((b) => b.classList.toggle('active', b.dataset['bf'] === currentFilter));
        refreshInstancesOnly();
      });
    });
    wireInstances();
  }

  openWideModal(
    t('bascule.modal.title', { name: appData.template_name }),
    modalBody(),
    async () => {
      closeModal();
      // bare-ref `router` est typé via core-globals.d.ts.
      if (typeof router?.dispatch === 'function') {
        router.dispatch();
      }
    },
    t('bascule.btn.finish'),
    true,
  );
  setTimeout(() => {
    wireBody();
  }, 0);
};

function renderBasculePage(
  appData: BasculeAppData,
  users: BasculeUser[],
  statuses: ActionStatusLite[],
  allProjects: ProjectMin[] = [],
): void {
  const userMap: Record<number, BasculeUser> = Object.fromEntries(users.map((u) => [u.id, u]));

  setHeader(
    `<a href="#project/${appData.project_id}" style="color:var(--accent);text-decoration:underline">Projet</a> / Bascule — ${esc(appData.template_name)}`,
    '',
  );

  const usedRoles = new Set<string>();
  appData.instances.forEach((inst) => {
    if (inst.accountable_role) usedRoles.add(inst.accountable_role);
    (inst.responsible_roles || []).forEach((r) => usedRoles.add(r));
    (inst.consulted_roles || []).forEach((r) => usedRoles.add(r));
    (inst.informed_roles || []).forEach((r) => usedRoles.add(r));
  });
  const roleList = [...usedRoles].sort();

  const mappings: Record<string, RoleMappingValue> = { ...appData.role_mappings };

  const renderMapping = (): string => {
    if (!roleList.length) return '';
    return `<div class="card" style="margin-bottom:12px">
      <div style="font-size:12px;font-weight:500;color:var(--text3);text-transform:uppercase;letter-spacing:.05em;margin-bottom:10px">Mapping global des rôles</div>
      <div style="font-size:12px;color:var(--text3);margin-bottom:10px">Assigne un utilisateur réel à un rôle générique sur toutes les actions non encore renseignées.</div>
      ${roleList
        .map((code) => {
          const mapped = mappings[code];
          return `<div style="display:flex;align-items:center;gap:8px;margin-bottom:6px">
          <span style="font-family:var(--font-mono);font-size:12px;font-weight:600;min-width:60px;background:var(--bg3);padding:2px 8px;border-radius:4px">${esc(code)}</span>
          <button class="btn btn-secondary btn-sm mapping-pick-btn" data-role="${esc(code)}" style="font-size:11px;flex-shrink:0">Choisir…</button>
          <span style="flex:1;font-size:12px">${
            mapped?.user_name
              ? `<span style="background:var(--accent-bg);color:var(--accent);font-size:12px;padding:2px 10px;border-radius:10px">${esc(mapped.user_name)}</span>`
              : '<span style="color:var(--text3)">Non assigné</span>'
          }</span>
          ${mapped?.user_id ? `<button class="btn btn-danger btn-icon mapping-clear-btn" data-role="${esc(code)}" style="font-size:11px" title="Effacer">✕</button>` : ''}
        </div>`;
        })
        .join('')}
      <div style="margin-top:10px;border-top:1px solid var(--border);padding-top:10px">
        <button class="btn btn-primary btn-sm" id="btn-apply-mappings">Appliquer le mapping à toutes les actions vides</button>
      </div>
    </div>`;
  };

  const renderProgress = (): string => {
    const pct = appData.pct ?? 0;
    const col = pct >= 100 ? 'var(--ok)' : pct >= 50 ? 'var(--accent)' : 'var(--warn)';
    return `<div class="card" style="margin-bottom:12px">
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:6px">
        <div style="font-size:12px;font-weight:500;color:var(--text3);text-transform:uppercase;letter-spacing:.05em">Progression</div>
        <span style="font-size:13px;font-weight:500">${appData.complete} / ${appData.total} actions complètes · ${pct} %</span>
      </div>
      <div style="height:8px;background:var(--bg3);border-radius:4px;overflow:hidden">
        <div style="height:100%;width:${pct}%;background:${col};transition:width .3s"></div>
      </div>
      <div style="font-size:11px;color:var(--text3);margin-top:6px">Complète = approbateur + réalisateurs + date butoir définis</div>
    </div>`;
  };

  // Ligne d'instance : rendu pur délégué au module frère (`userMap` immuable threadé).
  const instanceRow = (inst: BasculeInstance): string => buildBasculeInstanceRow(inst, userMap);

  const html = `
    ${renderProgress()}
    ${renderMapping()}
    <div style="display:flex;align-items:center;gap:6px;margin-bottom:8px">
      <div style="font-size:12px;font-weight:500;color:var(--text3);text-transform:uppercase;letter-spacing:.05em;flex:1">Actions (${appData.instances.length})</div>
      <button class="btn btn-secondary btn-sm filter-bascule active" data-bf="all">Toutes</button>
      <button class="btn btn-secondary btn-sm filter-bascule" data-bf="incomplete">Incomplètes</button>
      <button class="btn btn-secondary btn-sm filter-bascule" data-bf="nodate">Sans date</button>
      <button class="btn btn-secondary btn-sm filter-bascule" data-bf="noresp">Sans approbateur</button>
    </div>
    <div id="bascule-instances">
      ${appData.instances.map(instanceRow).join('')}
    </div>`;

  setContent(html);

  let currentFilter = 'all';

  const reloadApp = async (): Promise<void> => {
    const fresh = (await api.getApplication(appData.id)) as BasculeAppData;
    Object.assign(appData, fresh);
    rerenderInstances();
    const firstCard = document.querySelector<HTMLElement>('.card:first-child');

    if (firstCard) firstCard.outerHTML = renderProgress();
  };

  const rerenderInstances = (): void => {
    let visible = appData.instances;
    if (currentFilter === 'incomplete')
      visible = visible.filter(
        (i) => !(i.approver_id && (i.performers || []).length && i.deadline),
      );
    if (currentFilter === 'nodate') visible = visible.filter((i) => !i.deadline);
    if (currentFilter === 'noresp') visible = visible.filter((i) => !i.approver_id);
    const el = document.getElementById('bascule-instances');
    if (el)
      el.innerHTML =
        visible.map(instanceRow).join('') ||
        `<p style="color:var(--text3);font-size:12px;padding:16px 0">${esc(t('bascule.empty.filter'))}</p>`;
    wireInstances();
  };

  function wireInstances(): void {
    document.querySelectorAll<HTMLButtonElement>('[data-edit-inst]').forEach((btn) => {
      btn.addEventListener('click', (e: Event) => {
        e.stopPropagation();
        void (async () => {
          const instId = parseInt(btn.dataset['editInst'] || '0');
          const inst = appData.instances.find((i) => i.id === instId);
          if (!inst) return;

          const actionData = await _loadInstanceAction(inst);

          if (!actionData) {
            toast(t('bascule.toast.openError'), 'error');
            return;
          }

          window.showEditActionModal(
            actionData as never,
            {
              users: users as never,
              statuses: statuses as never,
              projects: allProjects as never,
              onSaved: async () => {
                await reloadApp();
              },
            } as never,
          );
        })();
      });
    });

    document.querySelectorAll<HTMLElement>('[data-inst-id]').forEach((card) => {
      card.addEventListener('click', (e: Event) => {
        const target = e.target as HTMLElement | null;
        if (target?.closest('button')) return;
        card.querySelector<HTMLButtonElement>('[data-edit-inst]')?.click();
      });
    });
  }

  document.getElementById('content')?.addEventListener('click', (e: Event) => {
    const target = e.target as HTMLElement | null;
    if (!target) return;
    const filterBtn = target.closest<HTMLButtonElement>('.filter-bascule');
    if (filterBtn) {
      currentFilter = filterBtn.dataset['bf'] || 'all';
      document
        .querySelectorAll<HTMLButtonElement>('.filter-bascule')
        .forEach((b) => b.classList.toggle('active', b.dataset['bf'] === currentFilter));
      rerenderInstances();
      return;
    }

    const pickBtn = target.closest<HTMLButtonElement>('.mapping-pick-btn');
    if (pickBtn) {
      const roleCode = pickBtn.dataset['role'] || '';
      window.showUserPicker(
        async (u: BasculeUser) => {
          mappings[roleCode] = { user_id: u.id, user_name: u.name };
          await api.setMappings(appData.id, {
            mappings: Object.entries(mappings).map(([role_code, v]) => ({
              role_code,
              user_id: v?.user_id || null,
            })),
          });
          appData.role_mappings = mappings;
          const mapCard = document.querySelector<HTMLElement>('.card:nth-child(2)');

          if (mapCard) mapCard.outerHTML = renderMapping();
        },
        { title: `Assigner le rôle ${roleCode}` },
      );
      return;
    }

    const clearBtn = target.closest<HTMLButtonElement>('.mapping-clear-btn');
    if (clearBtn) {
      const roleCode = clearBtn.dataset['role'] || '';
      void (async () => {
        delete mappings[roleCode];
        await api.setMappings(appData.id, {
          mappings: Object.entries(mappings).map(([role_code, v]) => ({
            role_code,
            user_id: v?.user_id || null,
          })),
        });
        appData.role_mappings = mappings;
        const mapCard = document.querySelector<HTMLElement>('.card:nth-child(2)');

        if (mapCard) mapCard.outerHTML = renderMapping();
      })();
      return;
    }

    if (target.id === 'btn-apply-mappings') {
      void (async () => {
        const result = (await api.applyMappings(appData.id)) as BasculeAppData & {
          changes?: number;
        };
        toast(t('bascule.toast.mappingAppliedWithCount', { n: result.changes ?? 0 }));
        Object.assign(appData, result);
        rerenderInstances();
      })();
    }
  });

  wireInstances();
}

// ── ESM globals shim (lot B.8 + S5.3) ──
window.renderBasculePage = renderBasculePage;

export {};
